import {
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'
import { classNames } from '@/util/tailwindHelpers'
import styles from './header.module.scss'
import { ModalTheme } from '@/types/modal'
import { isHeaderObject } from '../util'
import useIsSticky from '@/hooks/useIsSticky'
import { transformNumbersToRange } from '@/util/numberHelpers'
import { ModalCloseButton } from '../ModalCloseButton'
import { flipLightDarkTheme } from '@/util/theme'
import Button from '@/components/Button'
import Icon from '@/components/Icon'
import { ModalTitleProps } from '../types'
import { getRefHeight } from '@/util/reactHelpers'

interface HeaderProps {
  overlayRef: MutableRefObject<HTMLDivElement | null>
  title: ReactNode | ModalTitleProps
  theme: ModalTheme
  isCloseDisabled: boolean
  onCancel: () => void
  hide: () => void
  onRetreat?: () => void
  rightSlot?: ReactNode
  headerHeightRef: MutableRefObject<number>
  spacing: number
}

const TRANSLATE_MAX = 0
const TRANSLATE_MIN = 100

const Header = ({
  overlayRef,
  title,
  theme,
  isCloseDisabled,
  onCancel,
  hide,
  onRetreat,
  rightSlot,
  headerHeightRef,
  spacing
}: HeaderProps) => {
  const [titleTranslateY, setTitleTranslateY] = useState(100)
  const titleOpacityRef = useRef<number>(0)

  const headerRef = useRef<HTMLDivElement | null>(null)

  const headerTitle = isHeaderObject(title) ? title.header : title
  const isHeaderSticky = useIsSticky(headerRef, {
    threshold: 0.999,
    rootMargin: '-1px 0px -0px 0px'
  })
  const headerClassNames = classNames(styles.header, {
    [styles.headerRevealOnScroll]: isHeaderSticky
  })

  const handleScroll = e => {
    // SETUP /////////////////////////////////
    const scrollPos = e.target.scrollTop
    const isHeaderAtTop = scrollPos >= spacing
    const translateStart = spacing
    const translateEnd = headerHeightRef.current + spacing

    // TITLE - translate /////////////////////
    if (scrollPos > translateEnd && titleTranslateY !== TRANSLATE_MAX) {
      setTitleTranslateY(TRANSLATE_MAX)
    } else if (
      (scrollPos < translateStart && titleTranslateY !== TRANSLATE_MIN) ||
      // extra assurance that the title moves away
      (!isHeaderAtTop && titleTranslateY !== TRANSLATE_MIN)
    ) {
      setTitleTranslateY(TRANSLATE_MIN)
    } else if (scrollPos > translateStart && scrollPos < translateEnd) {
      setTitleTranslateY(
        Math.ceil(
          transformNumbersToRange(
            scrollPos,
            translateStart + spacing,
            translateEnd,
            TRANSLATE_MIN,
            TRANSLATE_MAX
          )
        )
      )
    }
    // TITLE - opacity ///////////////////////
    if (scrollPos > translateEnd && titleOpacityRef.current !== 1) {
      titleOpacityRef.current = 1
    } else if (scrollPos < translateStart && titleOpacityRef.current !== 0) {
      titleOpacityRef.current = 0
    } else if (scrollPos > translateStart && scrollPos < translateEnd) {
      const opacity = transformNumbersToRange(
        scrollPos,
        translateStart + spacing,
        translateEnd,
        0,
        1
      )
      titleOpacityRef.current = opacity
    }
  }

  // add listeners
  useEffect(() => {
    if (!overlayRef.current) return
    overlayRef.current.addEventListener('scroll', handleScroll)

    return () => {
      if (!overlayRef.current) return
      overlayRef.current.removeEventListener('scroll', handleScroll)
    }
  }, [overlayRef.current])

  // set header height
  useEffect(() => {
    if (!headerRef.current) return
    headerHeightRef.current = getRefHeight(headerRef)
    overlayRef.current.style.setProperty(
      '--headerHeight',
      `${headerHeightRef.current}px`
    )
  }, [headerRef.current])

  return (
    <header ref={headerRef} className={headerClassNames}>
      <ModalCloseButton
        theme={flipLightDarkTheme(theme)}
        isSticky={isHeaderSticky}
        isDisabled={isCloseDisabled}
        onClick={() => {
          onCancel?.()
          hide()
        }}
      />
      {headerTitle && (
        <h1
          className={classNames(styles.header_title, 'font-black uppercase')}
          style={
            {
              '--titleTranslateY': `${titleTranslateY}%`,
              '--titleOpacity': titleOpacityRef.current
            } as CSSProperties
          }
        >
          {headerTitle}
        </h1>
      )}
      {rightSlot && <div className="pr-[var(--modalPadding)]">{rightSlot}</div>}
      {onRetreat && (
        <div className="z-10 pr-[var(--modalPadding)]">
          <Button
            theme="gray-blur"
            onClick={onRetreat}
            size="xs"
            className="flex items-center !rounded-full"
          >
            <div className="flex gap-1 items-center">
              <Icon name="chevronLeftOutline" />
              <span className="utility-sm">Back</span>
            </div>
          </Button>
        </div>
      )}
    </header>
  )
}

export default Header
